import * as Sentry from '@sentry/nextjs'

import { IncidentQuery } from '@/graphql/generated/operations'
import {
  EventType,
  IncidentActionType,
  IncidentType,
  ResolutionSegmentType,
} from '@/graphql/generated/schemas'
import { DOWN, SCROLL, UP } from '@/utils/analytics'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'
import { IncidentTypeSlug } from '@/utils/incidents'

import { EventsEdgeT, EventsT } from '../types/traceLogTypes'

export interface HighlightedEventI {
  externalId: string
  detectedAt: string
}

type IncidentActions = IncidentQuery['incident']['actions']

// Canonical ITD tailgating slug plus the legacy aliases that older ITDs may
// still carry — kept in step with the TRACE_LOG_SLUGS aliases in the feed
// utils so an incident never gets the trace-logs entry point without the
// Incident tab.
const TAILGATING_SLUGS: string[] = [
  IncidentTypeSlug.DoorUnlockedTailgate,
  IncidentType.Tailgating,
  'tailgating',
]

// An ITD-flagged incident should always carry an incidentType slug; the
// fallback in getIsTailgatingIncident keeps the UI working when it doesn't,
// but that state points at a backfill/data gap we need visibility into.
// No user surface — nothing is broken for the operator. Guarded per incident
// id so the drawer's 3s poll and re-renders report each incident once.
const reportedMissingSlugIncidentIds = new Set<string>()

// Bounds the dedupe cache for long-lived operator sessions. Clearing on
// overflow means an already-seen incident may report once more, which
// Sentry's fingerprinting groups into the same issue.
export const MAX_REPORTED_MISSING_SLUG_IDS = 500

const reportMissingItdSlug = (incident: IncidentQuery['incident']): void => {
  const incidentId = incident.id
  if (!incidentId || reportedMissingSlugIncidentIds.has(incidentId)) return
  if (reportedMissingSlugIncidentIds.size >= MAX_REPORTED_MISSING_SLUG_IDS) {
    reportedMissingSlugIncidentIds.clear()
  }
  reportedMissingSlugIncidentIds.add(incidentId)
  Sentry.captureMessage(
    'ITD flag is on but the incident has no incidentType slug',
    {
      level: 'warning',
      tags: {
        feature: 'incident_drawer',
        component: 'trace_logs',
        errorType: 'missing_itd_slug',
      },
      extra: {
        incidentId,
        incidentType: incident.type,
        hasIncidentTypeDefinition: Boolean(incident.incidentType),
      },
    }
  )
}

/**
 * PACS integrations (e.g. OnGuard) report the string '0' as the badge value
 * for events with no real credential, so a truthiness check isn't enough —
 * treat '0' the same as a missing badge and omit the credential row
 * (ENG-32092).
 */
export const getDisplayBadgeNumber = (
  badgeNumber?: string | null
): string | undefined =>
  badgeNumber && badgeNumber !== '0' ? badgeNumber : undefined

/**
 * Tailgating detection for the incident detail panel and the trace-log
 * Incident tab. Under ITDs the discriminator is the incidentType slug
 * (`door-unlocked-tailgate`), not the legacy IncidentType enum value — the
 * two use different vocabularies (ENG-31994). Branches on slug presence with
 * a fallback to the incident type, mirroring shouldShowTraceLogs, so a
 * legacy incident that predates ITDs is still recognized when the flag is on.
 */
export const getIsTailgatingIncident = (
  incident: IncidentQuery['incident'] | undefined,
  isIncidentTypeDefinition: boolean
): boolean => {
  const slug = isIncidentTypeDefinition ? incident?.incidentType?.slug : null
  if (slug != null) return TAILGATING_SLUGS.includes(slug)
  // `incident` is undefined while the drawer is still loading — only a
  // loaded incident missing its slug is a reportable data gap.
  if (isIncidentTypeDefinition && incident) reportMissingItdSlug(incident)
  return incident?.type === IncidentType.Tailgating
}

/**
 * Extracts event references from resolved incident actions.
 *
 * Filters incident actions to find those with type RESOLVED, then extracts
 * externalId and detectedAt from EVENT_REF segments in the resolutionContent.
 *
 * @param actions - Array of incident actions
 * @returns Array of event references with externalId and detectedAt
 */
export const getResolvedActionEventRefs = (
  actions: IncidentActions
): HighlightedEventI[] => {
  if (!actions) return []

  return actions.flatMap((action) => {
    if (action?.type !== IncidentActionType.Resolved) return []

    const segments = action.resolutionContent?.segments ?? []

    return segments
      .filter(
        (segment) =>
          segment.segmentType === ResolutionSegmentType.EventRef &&
          segment.externalId &&
          segment.detectedAt
      )
      .map((segment) => ({
        externalId: segment.externalId!,
        detectedAt: segment.detectedAt!,
      }))
  })
}

/**
 * Checks if an event is highlighted from a resolution EVENT_REF click.
 *
 * @param event - The event node to check
 * @param highlightedEvent - The currently highlighted event from resolution (if any)
 * @returns True if the event matches the highlighted event
 */
export const isHighlightedFromResolution = (
  event: Pick<EventsT, 'externalId' | 'detectedAt'>,
  highlightedEvent: HighlightedEventI | null
): boolean => {
  return (
    highlightedEvent?.externalId === event.externalId &&
    highlightedEvent?.detectedAt === event.detectedAt
  )
}

/**
 * Checks if an event matches any of the resolved action event references.
 *
 * @param event - The event node to check
 * @param resolvedActionEventRefs - Array of event refs from resolved actions
 * @returns True if the event matches any resolved action event ref
 */
export const matchesResolvedActionEventRef = (
  event: Pick<EventsT, 'externalId' | 'detectedAt'>,
  resolvedActionEventRefs: HighlightedEventI[]
): boolean => {
  return resolvedActionEventRefs.some(
    (ref) =>
      ref.externalId === event.externalId && ref.detectedAt === event.detectedAt
  )
}

/**
 * Filters events for the Incident tab display.
 *
 * When filtering is enabled, includes events that either:
 * - Match the incident's associated event IDs (e.g., tailgating events)
 * - Match any event reference from resolved actions
 *
 * @param events - Array of event edges to filter
 * @param showOnlyIncidentRelatedEvents - Whether to apply filtering
 * @param incidentEventExternalIds - Event IDs associated with the incident
 * @param resolvedActionEventRefs - Event refs from resolved actions to include
 * @returns Filtered array of events, or all events if filtering is disabled
 */
export const filterEventsForIncidentTab = (
  events: EventsEdgeT[] | undefined,
  showOnlyIncidentRelatedEvents: boolean,
  incidentEventExternalIds: string[] | undefined,
  resolvedActionEventRefs: HighlightedEventI[] = []
): EventsEdgeT[] | undefined => {
  if (!showOnlyIncidentRelatedEvents) {
    return events
  }

  return events?.filter((e) => {
    if (matchesResolvedActionEventRef(e.node, resolvedActionEventRefs)) {
      return true
    }
    return incidentEventExternalIds?.includes(e.node.externalId) ?? false
  })
}

/**
 * Resolves the Trace Logs drawer's initial tab.
 *
 * The Incident tab (index 0) only renders when `shouldShowIncidentTab`, which
 * shifts the Device tab to index 1. The Reasoning Trail deep-link forces the
 * Device tab; otherwise the Incident tab is selected when shown (pill deep-link
 * / tailgating), and `undefined` lets the Tabs component use its own default.
 *
 * @returns The `defaultIndex` for the Tabs component, or `undefined`.
 */
export const getTraceLogDefaultTabIndex = (
  shouldForceDeviceTab: boolean,
  shouldShowIncidentTab: boolean
): number | undefined => {
  const incidentTabIndex = 0
  const deviceTabIndex = shouldShowIncidentTab ? 1 : 0

  if (shouldForceDeviceTab) {
    return deviceTabIndex
  }
  if (shouldShowIncidentTab) {
    return incidentTabIndex
  }
  return undefined
}

// Mixpanel events
export const VIEW_TL_EVENT = 'View trace logs'
export const VIEW_TL_TAB_EVENT = 'tab - trace logs'
export const DEVICES_TAB_EVENT = 'Devices'
export const FACILITIES_TAB_EVENT = 'Facilities'
export const INCIDENT_TAB_EVENT = 'Incident'
export const ADDITIONAL_TL_EVENTS = 'viewing additional trace log events'
export const SCROLL_UP_EVENT = `${SCROLL} ${UP} ${ADDITIONAL_TL_EVENTS}`
export const SCROLL_DOWN_EVENT = `${SCROLL} ${DOWN} ${ADDITIONAL_TL_EVENTS}`

/**
 * Returns a formatted event name by capitalizing and removing underscores from the event type.
 * If the event type is 'Other' or null, attempts to use the ACS event type.
 * Returns 'Unknown' if neither type is available.
 *
 * @param event - The event object to extract the name from.
 * @returns The formatted event name or 'Unknown'.
 */
export const getEventName = (event: EventsT) => {
  if (event?.type !== EventType.Other && event?.type !== null) {
    return removeUnderscoreAndCapitalize(event?.type)
  }
  if (event?.acsEventType) {
    return removeUnderscoreAndCapitalize(event?.acsEventType)
  }
  return 'Unknown'
}

export const getMixpanelEventData = (incident: IncidentQuery['incident']) => ({
  device_id: incident?.devices[0]?.id,
  facility_id: incident?.facility?.id,
  incident_id: incident?.id,
})
